// React and Router imports
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select"

// Firebase imports
import { auth } from "../../firebase.js";

// Component imports
import { useContextMenu } from "./ContextMenu";
import ConvertDocumentModal from "./ConvertDocumentModal";
import NewFromTemplateButton from "./templates/NewFromTemplateButton";
import { useSidebarWidth, SidebarResizeHandle } from "./SidebarResizeHandle";

// Styles
import "../../css/Sidebar.css";

function Sidebar({ viewingSharedDocument = false }) {
        const [collapsed, setCollapsed] = useState(() => {
                return window.innerWidth > 600;
        });
    const [pageDetails, setPageDetails] = useState([]);
    const [pageDetailsForSharedDocuments, setPageDetailsForSharedDocuments] = useState([])
    const [archivedPageDetails, setArchivedPageDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uid, setUid] = useState("");
    const [userEmail, setUserEamil] = useState("")
    const [hasDocumentation, setHasDocumentation] = useState(true);
    const [expandedSpaces, setExpandedSpaces] = useState({});
    const [viewingSharedDocuments, setViewingSharedDocuments] = useState(viewingSharedDocument);
    const [viewingArchivedDocuments, setViewingArchivedDocuments] = useState(false);
    const [hasSharedDocumentation, setHasSharedDocumentation] = useState(true);
    const [hasArchivedDocumentation, setHasArchivedDocumentation] = useState(false);
    const [searchOptions, setSearchOptions] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [authResolved, setAuthResolved] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const { width: sidebarWidth, isResizing, setIsResizing, applyWidth, resetWidth } = useSidebarWidth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleArchiveDocument = async (type, name, authorUid, isCurrentlyArchived) => {
        const action = isCurrentlyArchived ? 'unarchive' : 'archive';
        const confirmMsg = isCurrentlyArchived
            ? `Unarchive this ${type}?`
            : `Archive this ${type}? All child documents will also be archived.`;
        if (!window.confirm(confirmMsg)) return;
        try {
            const user = auth.currentUser;
            if (!user) { alert("You must be logged in."); return; }
            const token = await user.getIdToken();
            const endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/${action}/${type}/${name}/${authorUid}/`;
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${token}`,
                    'Auth-Token': `Bearer ${token}`,
                    'Requesting-User-Uid': user.uid
                }
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || `Failed to ${action} document`);
            }
            window.location.reload();
        } catch (error) {
            console.error(`Error ${action}ing document:`, error);
            alert(`Error: ${error.message}`);
        }
    };

    // Create dynamic context menu options for sidebar items
    const getSidebarItemContextOptions = (itemData) => {
        if (!itemData) return [];

        const { type, name, uid: itemUid, isArchived, isOwner } = itemData;
        const url = `/documentation/${type}/${name}/${itemUid}`;

        const options = [
            {
                label: "Open",
                icon: <i className="fas fa-arrow-right"></i>,
                onClick: () => navigate(url)
            },
            {
                label: "Open in New Tab",
                icon: <i className="fas fa-external-link-alt"></i>,
                url: url,
                onClick: () => window.open(url, "_blank", "noopener,noreferrer")
            },
            {
                label: "Open in New Window",
                icon: <i className="fas fa-window-maximize"></i>,
                onClick: () => window.open(url, "_blank", "width=1200,height=800,noopener,noreferrer")
            },
            {
                label: "Copy Link",
                icon: <i className="fas fa-copy"></i>,
                onClick: () => {
                    const fullUrl = `${window.location.origin}${url}`;
                    navigator.clipboard.writeText(fullUrl);
                    alert("Link copied to clipboard!");
                }
            },
            {
                label: "Convert to Workspace…",
                icon: <i className="fas fa-exchange-alt"></i>,
                onClick: () => setShowConvertModal(true)
            }
        ];

        if (isOwner) {
            options.push({
                label: isArchived ? "Unarchive" : "Archive",
                icon: <i className={`fas ${isArchived ? 'fa-box-open' : 'fa-box-archive'}`}></i>,
                onClick: () => handleArchiveDocument(type, name, itemUid, isArchived)
            });
        }

        return options;
    };

    const { showContextMenu, ContextMenuComponent } = useContextMenu(getSidebarItemContextOptions);

    // Wrap showContextMenu to ensure proper event handling
    const handleSidebarItemContextMenu = (e, itemData) => {
        e.preventDefault();
        e.stopPropagation();
        showContextMenu(e.clientX, e.clientY, itemData);
    };

    // Helper function to check if a link is currently active
    const isLinkActive = (linkPath) => {
        // Encode the linkPath to match how URLs appear in the browser
        const encodedLinkPath = linkPath.split('/').map((segment, index) => {
            // Don't encode the first few segments (/documentation/page or /documentation/space)
            if (index < 3) return segment;
            // Encode the name segment and uid segment
            return encodeURIComponent(segment);
        }).join('/');
        
        return location.pathname === encodedLinkPath;
    };
    // Auto-expand all ancestor nodes (pages and space) when viewing a page
    useEffect(() => {
        // Match /documentation/space/:spaceName/:uid
        const spaceMatch = location.pathname.match(/^\/documentation\/space\/([^/]+)\//);
        // Match /documentation/page/:pageName/:uid
        const pageMatch = location.pathname.match(/^\/documentation\/page\/([^/]+)\//);
        if (spaceMatch && pageDetails.length > 0) {
            const spaceName = decodeURIComponent(spaceMatch[1]);
            const found = pageDetails.find(
                (space) => (space.space === spaceName || space.page_name === spaceName)
            );
            if (found) {
                setExpandedSpaces((prev) => ({ ...prev, [found.id]: true }));
            }
        } else if (pageMatch && pageDetails.length > 0) {
            const pageName = decodeURIComponent(pageMatch[1]);
            // Recursively find all ancestor ids (pages and space)
            const findAncestors = (nodes, targetName, path = []) => {
                for (const node of nodes) {
                    const currentPath = [...path, node.id];
                    if (node.page_name === targetName) {
                        return currentPath;
                    }
                    if (node.children && node.children.length > 0) {
                        const result = findAncestors(node.children, targetName, currentPath);
                        if (result) return result;
                    }
                }
                return null;
            };
            const ancestorIds = findAncestors(pageDetails, pageName);
            if (ancestorIds && ancestorIds.length > 0) {
                // Exclude only the last id if it is the page itself (i.e., the leaf node)
                // But expand all parent pages and the space
                // ancestorIds: [spaceId, parentPageId, ..., currentPageId]
                // We want to expand all except the last if the last is the current page
                const toExpand = ancestorIds.length > 1 ? ancestorIds.slice(0, -1) : ancestorIds;
                setExpandedSpaces((prev) => {
                    const expanded = { ...prev };
                    toExpand.forEach(id => { expanded[id] = true; });
                    return expanded;
                });
            }
        }
    }, [location.pathname, pageDetails]);
    
    // Add a useEffect to respond to the prop changes
    useEffect(() => {
        if (viewingSharedDocument) {
            setViewingSharedDocuments(true);

            // Fetch shared documents if we're viewing a shared document
            if (uid) {
                fetchPageDetailsForSharedDocuments();
            }
        }
    }, [viewingSharedDocument, uid]);

    // Keep the current document visible in the tree when navigating
    useEffect(() => {
        const activeLink = document.querySelector(".sidebar .Sidebar__active-link");
        if (activeLink && typeof activeLink.scrollIntoView === "function") {
            activeLink.scrollIntoView({ block: "nearest" });
        }
    }, [location.pathname, loading]);

    const toggleSidebar = () => { setCollapsed(!collapsed); };

    const handleNavigateClick = () => { navigate("/create") }

    const handleResize = () => {
        const isMobile = window.innerWidth <= 600;
        const wasCollapsed = collapsed;
        
        // Only auto-adjust if we're crossing the mobile/desktop threshold
        // to prevent keyboard events from closing the sidebar
        if (isMobile && !wasCollapsed) {
            // Don't auto-collapse on mobile if user manually opened it
            return;
        }
        
        setCollapsed(window.innerWidth > 600);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [collapsed]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
                setUserEamil(user.email)
            } else {
                setUid(null);
                setUserEamil(null)
            }
            setAuthResolved(true);
        });

        return () => { unsubscribe(); };
    }, []);

    useEffect(() => {
        const fetchPageDetails = async () => {
            if (!uid) return;
            try {
                const user = auth.currentUser;
                const token = user ? await user.getIdToken() : null;
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/page-details/author/${uid}/`, {
                    headers: {
                        ...(token && { 'Authorization': `${token}` })
                    }
                });

                if (response.status === 404) {
                    setHasDocumentation(false);
                    setLoading(false);
                    return;
                }

                const data = await response.json();
                organizePageData(data);
                prepareSearchOptions(data);
                setLoading(false);
                setHasDocumentation(true);
            } catch (error) {
                console.error("Error fetching page details:", error);
            }
        };

        fetchPageDetails();
    }, [uid]);

    const fetchArchivedDocuments = async () => {
        if (!uid) return;
        try {
            const user = auth.currentUser;
            if (!user) return;
            const token = await user.getIdToken();
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/page-details/archived/author/${uid}/`, {
                headers: {
                    'Authorization': `${token}`,
                    'Auth-Token': `Bearer ${token}`,
                    'Requesting-User-Uid': uid
                }
            });
            if (response.status === 404) {
                setHasArchivedDocumentation(false);
                return;
            }
            const data = await response.json();
            organizeArchivedPageData(data);
            setHasArchivedDocumentation(true);
        } catch (error) {
            console.error("Error fetching archived documents:", error);
            setHasArchivedDocumentation(false);
        }
    };

        const fetchPageDetailsForSharedDocuments = async () => {
        if (!uid) return
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/documentation/shared-documents/", {
                method: "GET",
                headers: {
                    'requesting-user-uid': uid,
                    'requesting-user-email': userEmail,
                    ...(token && { 'Authorization': `${token}` })
                }
            })

            if (response.status === 404) {
                setHasSharedDocumentation(false);
                return;
            }

            const data = await response.json()
            const isEmpty = Array.isArray(data)
                ? data.length === 0 
                : Object.keys(data).length === 0; 
            if (isEmpty) {
                setHasSharedDocumentation(false)
                return
            }
            organizePageDataForSharedDocuments(data)
            prepareSearchOptionsForShared(data);
            setHasSharedDocumentation(true)
        } catch (error) {
            console.error("Error fetching page details for shared documents: ", error)
        }
        }

    const handleHelpClick = () => { navigate("/help") }

    const handleDocumentSearchClick = () => { 
        setShowSearch(!showSearch);
    }

    const handleSearchSelect = (selected) => {
        if (selected) {
            const { type, name, uid } = selected.value;
            navigate(`/documentation/${type}/${name}/${uid}/`);
            setShowSearch(false);
        }
    }

    const organizePageDataForSharedDocuments = (data) => {
        const organizedData = {};
        const orphanPages = []; // Store pages with missing parents temporarily
    
        // First pass: Create entries for spaces and any pages that don't have a parent
        data.forEach((item) => {
            if (!item.parent_page) {
                // This is a top-level space or page
                organizedData[item.space || item.page_name] = { ...item, children: [] };
            } else {
                // If it has a parent_page, assume it's an orphan for now
                orphanPages.push(item);
            }
        });
    
        // Recursive helper function to find the correct parent and add the item
        const findParentAndAddChild = (parentName, child) => {
            // Loop through organizedData to find the parent recursively
            for (const key in organizedData) {
                const result = addChildToParent(organizedData[key], parentName, child);
                if (result) return true;
            }
            return false;
        };
    
        const addChildToParent = (node, parentName, child) => {
            if (node.page_name === parentName || node.space === parentName) {
                // If this node is the parent, add the child here
                node.children.push({ ...child, children: [] });
                return true;
            }
            // Otherwise, recursively check each child node
            for (const subNode of node.children) {
                const found = addChildToParent(subNode, parentName, child);
                if (found) return true;
            }
            return false;
        };
    
        // Second pass: Place each page under its parent or treat it as a top-level page if the parent is missing
        orphanPages.forEach((item) => {
            if (!findParentAndAddChild(item.parent_page, item)) {
                // If the parent is not found, treat it as a top-level page
                organizedData[item.page_name] = { ...item, children: [] };
            }
        });
        setPageDetailsForSharedDocuments(Object.values(organizedData));
    };    

    const prepareSearchOptions = (data) => {
        const options = data.map(item => ({
            value: {
                id: item.id,
                type: item.page_name ? 'page' : 'space',
                name: item.page_name || item.space,
                uid: uid
            },
            label: `${item.page_name ? 'Page' : 'Space'}: ${item.page_name || item.space}`
        }));
        
        setSearchOptions(prevOptions => {
            // Filter out shared documents if they exist in the current options
            const filteredPrev = prevOptions.filter(option => 
                option.value.uid !== uid
            );
            return [...filteredPrev, ...options];
        });
    };

    const prepareSearchOptionsForShared = (data) => {
        const options = data.map(item => ({
            value: {
                id: item.id,
                type: item.page_name ? 'page' : 'space',
                name: item.page_name || item.space,
                uid: item.document_uid
            },
            label: `Shared ${item.page_name ? 'Page' : 'Space'}: ${item.page_name || item.space}`
        }));
        
        setSearchOptions(prevOptions => {
            // Filter out user's own documents if they exist in the current options
            const filteredPrev = prevOptions.filter(option => 
                option.value.uid === uid
            );
            return [...filteredPrev, ...options];
        });
    };

    const organizePageData = (data) => {
        const organizedData = {};
        const allItems = new Map(data.map(item => [item.space || item.page_name, item]));
        const placedItems = new Set(); // Track which items have been placed

        // First pass: Create entries for spaces and parentless pages
        data.forEach(item => {
            if (!item.parent_page) {
                organizedData[item.space || item.page_name] = { ...item, children: [] };
                placedItems.add(item.id);
            }
        });

        const findParentAndAddChild = (parentName, child) => {
            if (placedItems.has(child.id)) return true; // Skip if already placed
            for (const key in organizedData) {
                const result = addChildToParent(organizedData[key], parentName, child);
                if (result) {
                    placedItems.add(child.id);
                    return true;
                }
            }
            return false;
        };

        const addChildToParent = (node, parentName, child) => {
            if (node.page_name === parentName || node.space === parentName) {
                node.children.push({ ...child, children: [] });
                return true;
            }
            for (const subNode of node.children) {
                const found = addChildToParent(subNode, parentName, child);
                if (found) return true;
            }
            return false;
        };

        // Second pass: Place pages with parents that are spaces
        data.forEach(item => {
            if (item.parent_page) {
                findParentAndAddChild(item.parent_page, item);
            }
        });

        // Third pass: Try again for any remaining unplaced pages
        // Skip orphaned pages whose parent no longer exists — these have a
        // parent_page value but no matching space/page in the data set.
        // Users can resolve them via "Check Data Integrity".
        data.forEach(item => {
            if (item.parent_page && !placedItems.has(item.id)) {
                const parentFound = findParentAndAddChild(item.parent_page, item);
                if (!parentFound) {
                    // Only promote to top-level if the item has a displayable name
                    // (i.e. it's a space or a standalone page, not an orphaned child)
                    const parentExistsInData = allItems.has(item.parent_page);
                    if (!parentExistsInData) {
                        // Parent was deleted — this is an orphaned page, don't show it
                        return;
                    }
                    organizedData[item.page_name] = { ...item, children: [] };
                }
            }
        });

        setPageDetails(Object.values(organizedData));
    };

    const toggleSpace = (spaceId) => {
        setExpandedSpaces(prev => ({
            ...prev,
            [spaceId]: !prev[spaceId]
        }));
    };
    
    const renderPages = (pages) => {
        return (
            <ul>
                {pages.map((page, idx) => (
                    <PageItem key={idx} page={page} uid={uid} expandedSpaces={expandedSpaces} toggleSpace={toggleSpace} isLinkActive={isLinkActive} onItemRightClick={handleSidebarItemContextMenu} />
                ))}
            </ul>
        );
    };    

        const renderSharedPages = (pages, documentUid) => {
                return (
                        <ul>
                                {pages.map((page, idx) => (
                                        <PageItem key={idx} page={page} uid={documentUid} expandedSpaces={expandedSpaces} toggleSpace={toggleSpace} isLinkActive={isLinkActive} onItemRightClick={handleSidebarItemContextMenu} />
                                ))}
                        </ul>
                )
        }

    const organizeArchivedPageData = (data) => {
        const organizedData = {};
        const orphanPages = [];

        data.forEach(item => {
            if (!item.parent_page) {
                organizedData[item.space || item.page_name] = { ...item, children: [] };
            } else {
                orphanPages.push(item);
            }
        });

        const findParentAndAddChild = (parentName, child) => {
            for (const key in organizedData) {
                const result = addChildToParent(organizedData[key], parentName, child);
                if (result) return true;
            }
            return false;
        };

        const addChildToParent = (node, parentName, child) => {
            if (node.page_name === parentName || node.space === parentName) {
                node.children.push({ ...child, children: [] });
                return true;
            }
            for (const subNode of node.children) {
                const found = addChildToParent(subNode, parentName, child);
                if (found) return true;
            }
            return false;
        };

        orphanPages.forEach(item => {
            if (!findParentAndAddChild(item.parent_page, item)) {
                organizedData[item.page_name] = { ...item, children: [] };
            }
        });

        setArchivedPageDetails(Object.values(organizedData));
    };

        const handleUsersIconClick = () => {
                setViewingSharedDocuments(true);
                setViewingArchivedDocuments(false);
                fetchPageDetailsForSharedDocuments();
        }

        const handleUserIconClick = () => {
                setViewingSharedDocuments(false);
                setViewingArchivedDocuments(false);
        }

        const handleArchiveIconClick = () => {
                setViewingSharedDocuments(false);
                setViewingArchivedDocuments(true);
                fetchArchivedDocuments();
        }

        // Which collection tab is active — drives the tab pill styling
        const activeCollection = viewingArchivedDocuments ? 'archived' : viewingSharedDocuments ? 'shared' : 'mine';

        const handleSearchClick = () => { navigate("/documentation/search") }

    if (!uid && authResolved) {
        return (
            <div>
                <button onClick={() => navigate("/login")} className="Sidebar__login-button">Login</button>
            </div>
        )
    }

    return (
        <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${isResizing ? 'resizing' : ''}`} style={{ "--sidebar-width": `${sidebarWidth}px` }}>
                <div className="Sidebar__search-icon-container">
                        <button onClick={handleSearchClick} className={`Sidebar__search-button ${collapsed ? '' : 'Sidebar__no-display'}`}><i class="fa-solid fa-magnifying-glass Sidebar__search-icon"></i>Public Documents</button>
                </div>
            <button onClick={toggleSidebar} className="toggle-button">
                <i className={`fas ${collapsed ? 'fa-arrow-left' : 'fa-arrow-right'}`}></i>
            </button>
            <div className={`Sidebar__icon-container ${collapsed ? 'Sidebar__icon-container': 'Sidebar__hide-content'}`}>
                <Link to="/">
                    <i className="fas fa-home Sidebar__icons" title="Home"></i>
                </Link>
                <Link to="/documentation">
                    <i className="fa-solid fa-table-columns Sidebar__icons" title="Dashboard"></i>
                </Link>
                <i id="user-icon" onClick={handleUserIconClick} className={`fa-solid fa-user Sidebar__icons ${activeCollection === 'mine' ? 'Sidebar__icon-active' : ''}`} title="View your documents."></i>
                <i id="users-icon" onClick={handleUsersIconClick} className={`fa-solid fa-users Sidebar__icons ${activeCollection === 'shared' ? 'Sidebar__icon-active' : ''}`} title="View documents shared with you."></i>
                <i id="archive-icon" onClick={handleArchiveIconClick} className={`fa-solid fa-box-archive Sidebar__icons ${activeCollection === 'archived' ? 'Sidebar__icon-active' : ''}`} title="View archived documents."></i>
            </div>

            <div className={`Sidebar__search-dropdown ${!collapsed ? 'Sidebar__search-dropdown-collapsed' : ''}`}>
                <Select
                    options={searchOptions}
                    onChange={handleSearchSelect}
                    placeholder="All Documents"
                    isClearable
                    className="Sidebar__search-select"
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                            ...base,
                            backgroundColor: 'var(--bb-bg)',
                            borderColor: 'var(--bb-text)',
                        }),
                        menu: (base) => ({
                            ...base,
                            backgroundColor: 'var(--bb-bg)',
                        }),
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused 
                                ? 'var(--bb-border)' 
                                : 'var(--bb-bg)',
                            color: 'var(--bb-text)',
                        }),
                    }}
                />
            </div>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <nav className={`${collapsed ? '': 'Sidebar__hide-content'}`}>
                    {viewingArchivedDocuments ? (
                        hasArchivedDocumentation && archivedPageDetails.length > 0 ? (
                            <ul className="Sidebar__ul">
                            {archivedPageDetails.map((item, index) => {
                                const docName = item.space || item.page_name;
                                const docType = item.page_name && !item.space ? 'page' : 'space';
                                const itemPath = `/documentation/${docType}/${docName}/${uid}`;
                                return (
                                    <li key={index}>
                                    <div className="Sidebar__item-container">
                                        {item.children && item.children.length > 0 && (
                                            <i
                                                className={`fas ${expandedSpaces[`arch_${item.id || index}`] ? 'fa-folder-open' : 'fa-folder'} Sidebar__space-icon Sidebar__space-icon-clickable`}
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSpace(`arch_${item.id || index}`); }}
                                                title="Click to expand/collapse"
                                            ></i>
                                        )}
                                        {(!item.children || item.children.length === 0) && (
                                            <i className="fas fa-folder Sidebar__space-icon"></i>
                                        )}
                                        <Link
                                            to={itemPath}
                                            title={docName}
                                            className={`Sidebar__link-wrapper ${isLinkActive(itemPath) ? 'Sidebar__active-link' : ''}`}
                                            onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                type: docType,
                                                name: docName,
                                                uid: uid,
                                                isArchived: true,
                                                isOwner: true
                                            })}
                                        >
                                            {docName}
                                        </Link>
                                    </div>
                                    {item.children && item.children.length > 0 && expandedSpaces[`arch_${item.id || index}`] && renderPages(item.children)}
                                    </li>
                                );
                            })}
                            </ul>
                        ) : (
                            <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>No archived documents.</p>
                        )
                    ) : hasDocumentation && viewingSharedDocuments !== true ? (
                            <ul className="Sidebar__ul">
                            {pageDetails.map((space, index) => {
                                const spacePath = `/documentation/${space.page_name ? 'page' : 'space'}/${space.space}/${uid}`;
                                const spaceEditPath = `${spacePath}/edit`;
                                return (
                                    <li key={index}>
                                    <div className="Sidebar__item-container">
                                        {space.children && space.children.length > 0 && (
                                            <i
                                                className={`fas ${expandedSpaces[space.id || index] ? 'fa-folder-open' : 'fa-folder'} Sidebar__space-icon Sidebar__space-icon-clickable`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleSpace(space.id || index);
                                                }}
                                                title="Click to expand/collapse"
                                            ></i>
                                        )}
                                        {(!space.children || space.children.length === 0) && (
                                            <i className="fas fa-folder Sidebar__space-icon"></i>
                                        )}
                                        <Link
                                            key={`${space.page_name || space.space}-${space.document_uid}`}
                                            to={spacePath}
                                            title={space.space}
                                            className={`Sidebar__link-wrapper ${isLinkActive(spacePath) ? 'Sidebar__active-link' : ''}`}
                                            onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                type: space.page_name ? 'page' : 'space',
                                                name: space.space,
                                                uid: uid,
                                                isArchived: false,
                                                isOwner: true
                                            })}
                                        >
                                            {space.space}
                                        </Link>
                                        <button
                                            className="Sidebar__edit-button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                navigate(spaceEditPath);
                                            }}
                                            title="Edit document"
                                        >
                                            <i className="fas fa-pen"></i>
                                        </button>
                                    </div>
                                    {space.children.length > 0 && expandedSpaces[space.id || index] && renderPages(space.children)}
                                    </li>
                                );
                            })}
                            </ul>
                        ) : hasSharedDocumentation && viewingSharedDocuments ? (
                            <ul className="Sidebar__ul">
                            {pageDetailsForSharedDocuments.map((item, index) => {
                                const itemPath = `/documentation/${item.page_name ? 'page' : 'space'}/${item.page_name || item.space}/${item.document_uid}`;
                                const itemEditPath = `${itemPath}/edit`;
                                return (
                                    <li key={index}>
                                    <div className="Sidebar__item-container">
                                        {item.children && item.children.length > 0 && (
                                            <i
                                                className={`fas ${expandedSpaces[`shared_${item.id || index}`] ? 'fa-folder-open' : 'fa-folder'} Sidebar__space-icon Sidebar__space-icon-clickable`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleSpace(`shared_${item.id || index}`);
                                                }}
                                                title="Click to expand/collapse"
                                            ></i>
                                        )}
                                        {(!item.children || item.children.length === 0) && (
                                            <i className="fas fa-folder Sidebar__space-icon"></i>
                                        )}
                                        <Link
                                            key={`${item.page_name || item.space}-${item.document_uid}`}
                                            to={itemPath}
                                            title={item.page_name || item.space}
                                            className={`Sidebar__link-wrapper ${isLinkActive(itemPath) ? 'Sidebar__active-link' : ''}`}
                                            onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                type: item.page_name ? 'page' : 'space',
                                                name: item.page_name || item.space,
                                                uid: item.document_uid,
                                                isArchived: false,
                                                isOwner: false
                                            })}
                                        >
                                            {item.page_name || item.space}
                                        </Link>
                                        <button
                                            className="Sidebar__edit-button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                navigate(itemEditPath);
                                            }}
                                            title="Edit document"
                                        >
                                            <i className="fas fa-pen"></i>
                                        </button>
                                    </div>
                                    {item.children.length > 0 && expandedSpaces[`shared_${item.id || index}`] && renderSharedPages(item.children, item.document_uid)}
                                    </li>
                                );
                            })}
                            </ul>
                    ) : viewingSharedDocuments && hasSharedDocumentation !== true ? (
                        <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>No shared documentation to show.</p>
                    ) : (
                        <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>No documentation to show.</p>
                    )}
                </nav>
            )}

        {collapsed && (
                <>
                        <div className="Sidebar__create-actions">
                                <button className="Sidebar__create-button" onClick={handleNavigateClick}>
                                        <i className="fas fa-plus Sidebar__plus-sign"></i>&nbsp; Create
                                </button>
                                <NewFromTemplateButton createPath="/create" className="Sidebar__create-button Sidebar__create-button--template">
                                        <i className="fas fa-puzzle-piece Sidebar__plus-sign"></i>&nbsp; Templates
                                </NewFromTemplateButton>
                        </div>
                        <div className="Sidebar__bottom-icons">
                            <i className="fa-solid fa-circle-question Sidebar__help" onClick={handleHelpClick} title="Help"></i>
                        </div>
                </>
        )}
        {ContextMenuComponent}
        {showConvertModal && (
            <ConvertDocumentModal
                isOpen={showConvertModal}
                onClose={() => setShowConvertModal(false)}
                preSelectedDirection="personal_to_workspace"
            />
        )}
        {collapsed && (
            <SidebarResizeHandle
                width={sidebarWidth}
                applyWidth={applyWidth}
                resetWidth={resetWidth}
                setIsResizing={setIsResizing}
            />
        )}
        </div>
    );
}

function PageItem({ page, uid, expandedSpaces, toggleSpace, isLinkActive, onItemRightClick }) {
    const navigate = useNavigate();
    const isExpanded = expandedSpaces[page.id] || false;
    const linkPath = `/documentation/${page.page_name ? 'page' : 'space'}/${page.page_name || page.space}/${uid}`;
    const editPath = `${linkPath}/edit`;

    const handleEditClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(editPath);
    };

    const handleContextMenu = (e) => {
        if (onItemRightClick) {
            e.preventDefault();
            e.stopPropagation();
            onItemRightClick(e, {
                type: page.page_name ? 'page' : 'space',
                name: page.page_name || page.space,
                uid: uid
            });
        }
    };

    return (
        <li>
            <div className="Sidebar__item-container">
                {/* Show the caret if the page has children */}
                {page.children && page.children.length > 0 ? (
                    <button
                        className="Sidebar__chevron-button"
                        onClick={() => toggleSpace(page.id)}
                        title={isExpanded ? "Collapse" : "Expand"}
                    >
                        <i className={`Sidebar__icon-size fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                    </button>
                ) : (
                    <i className="fas fa-file Sidebar__page-icon"></i>
                )}
                <Link
                    to={linkPath}
                    className={`Sidebar__link-wrapper ${isLinkActive(linkPath) ? 'Sidebar__active-link' : ''}`}
                    onContextMenu={handleContextMenu}
                >
                    <p className="Sidebar__link" title={page.page_name || page.space}>{page.page_name || page.space}</p>
                </Link>
                <button
                    className="Sidebar__edit-button"
                    onClick={handleEditClick}
                    title="Edit document"
                >
                    <i className="fas fa-pen"></i>
                </button>
            </div>
            {isExpanded && page.children && page.children.length > 0 && (
                <ul>
                    {page.children.map((child, idx) => (
                        <span className="Sidebar__text" title={child.page_name || child.space}>
                            <PageItem key={idx} page={child} uid={uid} expandedSpaces={expandedSpaces} toggleSpace={toggleSpace} isLinkActive={isLinkActive} onItemRightClick={onItemRightClick} />
                        </span>
                    ))}
                </ul>
            )}
        </li>
    );
} 
export default Sidebar;
